import { interpolate, Html5Audio, useRemotionEnvironment } from "remotion";
import { type SoundOverlay } from "../../../types";
import { toAbsoluteUrl } from "../../general/url-helper";
import { useEditorContext } from "../../../contexts/editor-context";
import { FPS } from "@/constants";
import { withCorsHardenedMediaSrc } from "./media-cors";
import { useCallback, useState } from "react";

interface SoundLayerContentProps {
  overlay: SoundOverlay;
  baseUrl?: string;
}

const normalizeVolume = (volume: unknown): number => {
  return typeof volume === "number" && Number.isFinite(volume) ? Math.min(Math.max(volume, 0), 3) : 1;
};

const normalizeSpeed = (speed: unknown): number => {
  return typeof speed === "number" && Number.isFinite(speed) ? Math.min(Math.max(speed, 0.25), 4) : 1;
};

/**
 * Hook to safely use editor context only when available
 */
const useSafeEditorContext = () => {
  try {
    return useEditorContext();
  } catch {
    return { baseUrl: undefined };
  }
};

export const SoundLayerContent: React.FC<SoundLayerContentProps> = ({ overlay, baseUrl }) => {
  const { isRendering } = useRemotionEnvironment();
  const { baseUrl: contextBaseUrl } = useSafeEditorContext();
  const [amplificationBlockedSrc, setAmplificationBlockedSrc] = useState<string | null>(null);
  const baseVolume = normalizeVolume(overlay.styles?.volume);
  const needsAmplification = baseVolume > 1;
  const isAmplificationBlocked = amplificationBlockedSrc === overlay.src;
  const canUseAmplifiedPlayback = needsAmplification && !isAmplificationBlocked;

  const handleAmplifiedMediaError = useCallback(() => {
    if (!canUseAmplifiedPlayback || isRendering) {
      return;
    }

    console.warn(`Amplified audio playback blocked by CORS for ${overlay.src}; falling back to native volume.`);
    setAmplificationBlockedSrc(overlay.src);
  }, [canUseAmplifiedPlayback, isRendering, overlay.src]);

  // Use prop baseUrl first, then context baseUrl
  const resolvedBaseUrl = baseUrl || contextBaseUrl;

  // Safety check - don't render Audio if src is missing
  if (!overlay.src || overlay.src.trim() === "") {
    console.warn("SoundLayerContent: No src provided for sound overlay", overlay);
    return null;
  }

  // Determine the audio source URL
  let audioSrc = overlay.src;

  // If it's an API route, use toAbsoluteUrl to ensure proper domain
  if (overlay.src.startsWith("/api/")) {
    audioSrc = toAbsoluteUrl(overlay.src, resolvedBaseUrl);
  }
  // If it's a relative URL and baseUrl is provided, use baseUrl
  else if (overlay.src.startsWith("/") && resolvedBaseUrl) {
    audioSrc = `${resolvedBaseUrl}${overlay.src}`;
  }
  // Otherwise use the toAbsoluteUrl helper for relative URLs
  else if (overlay.src.startsWith("/")) {
    audioSrc = toAbsoluteUrl(overlay.src, resolvedBaseUrl);
  }

  const totalFrames = overlay.durationInFrames || 0;
  const totalDurationSeconds = totalFrames / FPS;

  // Calculate volume with fade in/out
  const fadeIn = Math.min(Math.max(0, overlay.styles?.fadeIn ?? 0), totalDurationSeconds);
  const fadeOut = Math.min(Math.max(0, overlay.styles?.fadeOut ?? 0), Math.max(0, totalDurationSeconds - fadeIn));

  // Calculate fade multiplier based on current frame
  // Assuming 30fps for fade calculation
  const fps = FPS;
  const fadeInFrames = fadeIn * fps;
  const fadeOutFrames = fadeOut * fps;

  const volumeForFrame = (volumeFrame: number) => {
    let fadeMultiplier = 1;

    if (fadeIn > 0 && volumeFrame < fadeInFrames) {
      const fadeInMultiplier = interpolate(volumeFrame, [0, fadeInFrames], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      fadeMultiplier *= fadeInMultiplier;
    }

    if (fadeOut > 0 && volumeFrame > totalFrames - fadeOutFrames) {
      const fadeOutMultiplier = interpolate(volumeFrame, [totalFrames - fadeOutFrames, totalFrames], [1, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      fadeMultiplier *= fadeOutMultiplier;
    }

    return (canUseAmplifiedPlayback ? baseVolume : Math.min(baseVolume, 1)) * fadeMultiplier;
  };

  const playbackRate = normalizeSpeed(overlay.speed);
  const audioPlaybackSrc = withCorsHardenedMediaSrc(audioSrc, canUseAmplifiedPlayback && !isRendering);

  return (
    <Html5Audio
      key={canUseAmplifiedPlayback ? "amplified" : "native"}
      src={audioPlaybackSrc}
      trimBefore={overlay.startFromSound || 0}
      volume={volumeForFrame}
      playbackRate={playbackRate}
      onError={handleAmplifiedMediaError}
      {...(canUseAmplifiedPlayback ? { crossOrigin: "anonymous" as const, useWebAudioApi: true } : {})}
    />
  );
};
